// src/core/config.ts

// --- Configuration des Environnements HDR ---
// Liste des noms de fichiers HDR disponibles dans public/models/environment/
export const hdrFiles: string[] = [
    'wildflower_field_1k.hdr',
    'rogland_clear_night_1k.hdr',
    'rainforest_trail_1k.hdr',
    'qwantani_night_1k.hdr',
    'ouchy_pier_1k.hdr',
    'moonless_golf_1k.hdr',
    'laufenurg_church_1k.hdr',
    'kloppenheim_02_1k.hdr',
    'klippad_dawn_2_1k.hdr',
    'dikhololo_night_1k.hdr',
    'autumn_field_puresky_4k.hdr',
    'golden_gate_hills_4k.hdr',
    'qwantani_dusk_2_4k.hdr',
    'horn-koppe_snow_4k.hdr'
];

// --- Configuration des Presets Manette ---
// Interface pour la structure des mappings
interface GamepadMappingCondition {
    type: 'button' | 'axis';
    index: number;
    threshold?: number;    // Pour axes ou boutons analogiques
    exactValue?: number;   // Pour D-Pad sur certains contrôleurs
}
interface GamepadMapping {
    [action: string]: GamepadMappingCondition[];
}
interface ControllerPreset {
    name: { [lang: string]: string };       // Noms traduits
    description: { [lang: string]: string }; // Descriptions traduites
    mapping: GamepadMapping;
}
interface ControllerPresets {
    [presetKey: string]: ControllerPreset;
}

// Définition des presets (Adapter les mappings à votre besoin)
export const controllerPresets: ControllerPresets = {
    default: {
        name: { fr: 'Défaut', en: 'Default', es: 'Por Defecto' },
        description: { fr: 'Contrôles standards.', en: 'Standard controls.', es: 'Controles estándar.' },
        mapping: {
            dpadUp:    [{ type: 'axis', index: 9, exactValue: -1 }, { type: 'button', index: 12 }],
            dpadDown:  [{ type: 'axis', index: 9, exactValue: 0.14 }, { type: 'button', index: 13 }],
            dpadLeft:  [{ type: 'axis', index: 9, exactValue: 0.71 }, { type: 'button', index: 14 }],
            dpadRight: [{ type: 'axis', index: 9, exactValue: -0.43 }, { type: 'button', index: 15 }],
            confirm:   [{ type: 'button', index: 0 }],
            backward:  [{ type: 'button', index: 1 }],
            pause:     [{ type: 'button', index: 9 }],
            leftStickX: [{ type: 'axis', index: 0 }],
            leftStickY: [{ type: 'axis', index: 1 }],
            rightStickX: [{ type: 'axis', index: 2 }],
            rightStickY: [{ type: 'axis', index: 3 }],
        }
    },
    classic: {
        name: {
            en: 'Classic Simple',
            fr: 'Classique Simple',
            es: 'Clásico Simple'
        },
        description: {
            en: 'Left Stick = Movement, Right Stick = Rotation.',
            fr: 'Stick G = Mouvement, Stick D = Rotation.',
            es: 'Stick Izq = Movimiento, Stick Der = Rotación.'
        },
        mapping: {
            yawLeft: [{ type: 'axis', index: 0, threshold: -0.2 }],
            yawRight: [{ type: 'axis', index: 0, threshold: 0.2 }],
            rollLeft: [{ type: 'axis', index: 2, threshold: -0.2 }],
            rollRight: [{ type: 'axis', index: 2, threshold: 0.2 }],
            pitchDown: [{ type: 'axis', index: 3, threshold: -0.2 }],
            pitchUp: [{ type: 'axis', index: 3, threshold: 0.2 }],
            forward: [{ type: 'button', index: 7 }],
            backward: [{ type: 'button', index: 1 }],
            boost: [{ type: 'button', index: 6 }],
            action: [{ type: 'button', index: 0 }],
            firePrimary: [{ type: 'button', index: 2 }],
            fireSecondary: [{ type: 'button', index: 3 }],
            pause: [{ type: 'button', index: 9 }],
            dpadUp: [{ type: 'button', index: 12 }],
            dpadDown: [{ type: 'button', index: 13 }],
            dpadLeft: [{ type: 'button', index: 14 }],
            dpadRight: [{ type: 'button', index: 15 }],
            confirm: [{ type: 'button', index: 0 }]
        }
    },
    lefty: {
        name: {
            en: 'Lefty',
            fr: 'Gaucher',
            es: 'Zurdo'
        },
        description: {
            en: 'Swaps horizontal sticks from Default.',
            fr: 'Inverse les sticks horizontaux du mode Défaut.',
            es: 'Invierte los sticks horizontales del modo Por Defecto.'
        },
        mapping: {
            rollLeft: [{ type: 'axis', index: 0, threshold: -0.2 }, { type: 'button', index: 4 }],
            rollRight: [{ type: 'axis', index: 0, threshold: 0.2 }, { type: 'button', index: 5 }],
            pitchDown: [{ type: 'axis', index: 1, threshold: -0.2 }, { type: 'axis', index: 3, threshold: -0.2 }],
            pitchUp: [{ type: 'axis', index: 1, threshold: 0.2 }, { type: 'axis', index: 3, threshold: 0.2 }],
            yawLeft: [{ type: 'axis', index: 2, threshold: -0.2 }],
            yawRight: [{ type: 'axis', index: 2, threshold: 0.2 }],
            forward: [{ type: 'button', index: 7, threshold: 0.1 }],
            backward: [{ type: 'button', index: 1 }],
            boost: [{ type: 'button', index: 6, threshold: 0.1 }],
            action: [{ type: 'button', index: 0 }],
            confirm: [{ type: 'button', index: 0 }],
            firePrimary: [{ type: 'button', index: 2 }],
            fireSecondary: [{ type: 'button', index: 3 }],
            pause: [{ type: 'button', index: 9 }],
            selectPrevPrimary: [{ type: 'axis', index: 9, threshold: -0.9 }],
            selectNextPrimary: [{ type: 'axis', index: 9, threshold: 0.9 }],
            selectPrevSecondary: [{ type: 'axis', index: 9, threshold: 0.6, exactValue: 0.714 }],
            selectNextSecondary: [{ type: 'axis', index: 9, threshold: -0.5, exactValue: -0.428 }],
            dpadUp: [{ type: 'button', index: 12 }],
            dpadDown: [{ type: 'button', index: 13 }],
            dpadLeft: [{ type: 'button', index: 14 }],
            dpadRight: [{ type: 'button', index: 15 }]
        }
    },
    triggerHappy: {
        name: {
            en: 'Trigger Happy',
            fr: 'As des Gâchettes',
            es: 'Gatillo Feliz'
        },
        description: {
            en: 'Main controls on L1/R1/L2/R2.',
            fr: 'Contrôles principaux sur L1/R1/L2/R2.',
            es: 'Controles principales en L1/R1/L2/R2.'
        },
        mapping: {
            forward: [{ type: 'button', index: 7, threshold: 0.1 }],
            boost: [{ type: 'button', index: 6, threshold: 0.1 }],
            yawLeft: [{ type: 'button', index: 4 }],
            yawRight: [{ type: 'button', index: 5 }],
            pitchUp: [{ type: 'axis', index: 3, threshold: 0.2 }],
            pitchDown: [{ type: 'axis', index: 3, threshold: -0.2 }],
            rollLeft: [{ type: 'axis', index: 0, threshold: -0.2 }],
            rollRight: [{ type: 'axis', index: 0, threshold: 0.2 }],
            backward: [{ type: 'button', index: 1 }],
            action: [{ type: 'button', index: 0 }],
            confirm: [{ type: 'button', index: 0 }],
            firePrimary: [{ type: 'button', index: 2 }],
            fireSecondary: [{ type: 'button', index: 3 }],
            pause: [{ type: 'button', index: 9 }],
            selectPrevPrimary: [{ type: 'axis', index: 9, threshold: -0.9 }],
            selectNextPrimary: [{ type: 'axis', index: 9, threshold: 0.9 }],
            selectPrevSecondary: [{ type: 'axis', index: 9, threshold: 0.6, exactValue: 0.714 }],
            selectNextSecondary: [{ type: 'axis', index: 9, threshold: -0.5, exactValue: -0.428 }],
            dpadUp: [{ type: 'button', index: 12 }],
            dpadDown: [{ type: 'button', index: 13 }],
            dpadLeft: [{ type: 'button', index: 14 }],
            dpadRight: [{ type: 'button', index: 15 }]
        }
    },
    invertedPitch: {
        name: {
            en: 'Inverted Pitch',
            fr: 'Tangage Inversé',
            es: 'Cabeceo Invertido'
        },
        description: {
            en: 'Inverted pitch controls (push stick = up).',
            fr: 'Contrôles de tangage inversés (pousser stick = monter).',
            es: 'Controles de cabeceo invertidos (empujar stick = subir).'
        },
        mapping: {
            yawLeft: [{ type: 'axis', index: 0, threshold: -0.2 }, { type: 'button', index: 4 }],
            yawRight: [{ type: 'axis', index: 0, threshold: 0.2 }, { type: 'button', index: 5 }],
            pitchUp: [{ type: 'axis', index: 1, threshold: -0.2 }, { type: 'axis', index: 3, threshold: -0.2 }],
            pitchDown: [{ type: 'axis', index: 1, threshold: 0.2 }, { type: 'axis', index: 3, threshold: 0.2 }],
            rollLeft: [{ type: 'axis', index: 2, threshold: -0.2 }],
            rollRight: [{ type: 'axis', index: 2, threshold: 0.2 }],
            forward: [{ type: 'button', index: 7, threshold: 0.1 }],
            backward: [{ type: 'button', index: 1 }],
            boost: [{ type: 'button', index: 6, threshold: 0.1 }],
            action: [{ type: 'button', index: 0 }],
            confirm: [{ type: 'button', index: 0 }],
            firePrimary: [{ type: 'button', index: 2 }],
            fireSecondary: [{ type: 'button', index: 3 }],
            pause: [{ type: 'button', index: 9 }],
            selectPrevPrimary: [{ type: 'axis', index: 9, threshold: -0.9 }],
            selectNextPrimary: [{ type: 'axis', index: 9, threshold: 0.9 }],
            selectPrevSecondary: [{ type: 'axis', index: 9, threshold: 0.6, exactValue: 0.714 }],
            selectNextSecondary: [{ type: 'axis', index: 9, threshold: -0.5, exactValue: -0.428 }],
            dpadUp: [{ type: 'button', index: 12 }],
            dpadDown: [{ type: 'button', index: 13 }],
            dpadLeft: [{ type: 'button', index: 14 }],
            dpadRight: [{ type: 'button', index: 15 }]
        }
    },
};

// Durations in milliseconds
export const COUNTDOWN_DURATION = 3000; // 3 seconds

// --- Adventure Mode Specific Config ---
export const ADVENTURE_PLAY_RADIUS = 500; // Rayon de la sphère de jeu (logique et visuel)
export const ADVENTURE_COLLECTIBLE_COUNT = 13; // Nombre de collectibles à générer
export const ADVENTURE_SPAWN_MIN_DISTANCE_FROM_CENTER = 50; // Distance minimale du centre (0,0,0) pour le spawn
export const ADVENTURE_OBSTACLE_SIZE = 10; // Taille (rayon) des obstacles sphériques

// --- AJOUT: Constantes de temps pour les modes Aventure (en millisecondes) ---
export const ADVENTURE_BASE_TIMER = 80000;     // 80 secondes pour le mode Aventure standard
export const ADVENTURE_CHAOS_TIMER = 50000;    // 50 secondes pour le mode Chaos
export const ADVENTURE_EXTREME_TIMER = 50000;  // 50 secondes pour le mode Extreme (ou moins si on veut plus tard)
// Le mode Exploration Libre utilisera un stopwatch (pas de limite)
// Le mode Custom Exploration lira sa valeur depuis les options du menu

// <<< AJOUT: Liste des modèles d'obstacles disponibles >>>
// (Basé sur les dossiers fournis: public/models/obstacles/)
export const ADVENTURE_OBSTACLE_MODELS = [
    'models/obstacles/namaqualand_boulder_03_1k/namaqualand_boulder_03_1k.gltf',
    'models/obstacles/namaqualand_boulder_04_1k/namaqualand_boulder_04_1k.gltf',
    'models/obstacles/namaqualand_boulder_05_1k/namaqualand_boulder_05_1k.gltf',
    'models/obstacles/namaqualand_boulder_06_1k/namaqualand_boulder_06_1k.gltf',
    // Ajoutez d'autres chemins de modèles ici si nécessaire
];

// <<< AJOUT: Min/Max nombre d'obstacles >>>
export const ADVENTURE_OBSTACLES_MIN = 80;     // Nombre d'obstacles au premier niveau
export const ADVENTURE_OBSTACLES_MAX = 100;    // Nombre d'obstacles au dernier niveau (anciennement ADVENTURE_OBSTACLE_COUNT)

// --- Visuals for Adventure Mode ---
export const ADVENTURE_SPHERE_OPACITY = 0.15; // Opacité de la sphère visuelle
export const ADVENTURE_SPHERE_COLOR = 0x87CEEB; // Couleur bleu ciel pour la sphère

// Add other game-wide constants here as needed
// Example:
// export const PLAYER_MAX_SPEED = 100; 